/**
 * What Talab says while it searches, written per sector from what the
 * customer actually asked for. A salesperson's language — never "thinking",
 * "processing", "loading", or a generic "searching products".
 */

export type SearchSpeed = 'fast' | 'normal' | 'slow' | 'verySlow'

/** Prototype-only: how long the simulated backend takes to finish. */
export const SEARCH_SPEED_MS: Record<SearchSpeed, number> = {
  fast: 2000,
  normal: 5000,
  slow: 10000,
  verySlow: 15000,
}

export type NarrationCategory =
  | 'cars'
  | 'car-rental'
  | 'property'
  | 'food'
  | 'apparel'
  | 'perfume'
  | 'furniture'
  | 'electronics'

export interface SearchNarration {
  /** Talab's first reply. Said once and never rewritten. */
  ack: string
  /** The one temporary status line shown while results arrive. */
  searching: string
  /** The single status change allowed, if the search runs long. */
  long: string
  /** Talab's new message once the results are in. */
  closing: string
  /** Plural noun for what's being shown, e.g. "fragrances", "homes". */
  noun: string
  /** Set only for open-ended "what do you have?" questions. */
  openQuestion?: string
}

/** Open-ended availability questions — the customer hasn't said what yet. */
export const isOpenQuestion = (text: string) =>
  /what do you (have|sell|carry|offer)|what'?s available|what is available|show me everything/i.test(
    text,
  )

/* ---------- Reading the request ---------- */

const cap = (s: string) => s.replace(/\b\w/g, (c) => c.toUpperCase())
const plural = (s: string) => (/s$/.test(s) ? s : /y$/.test(s) ? `${s.slice(0, -1)}ies` : `${s}s`)

/** "under AED 5,000", "under $150" — kept as the customer wrote the number. */
function budgetOf(t: string): string {
  const m = t.match(
    /\b(under|below|less than|up to|max(?:imum)?|within)\s*(aed|usd|\$)?\s*([\d][\d,.]*\s*k?)/,
  )
  if (!m) return ''
  const cur = m[2] ? (m[2] === '$' ? '$' : `${m[2].toUpperCase()} `) : ''
  return `under ${cur}${m[3].trim().replace(/[.,]+$/, '').replace(/k$/, 'K')}`
}

const AREAS = [
  'dubai marina',
  'marina',
  'downtown dubai',
  'downtown',
  'business bay',
  'jvc',
  'jumeirah village circle',
  'jlt',
  'jbr',
  'palm jumeirah',
  'arabian ranches',
  'dubai hills',
  'al barsha',
  'town square',
  'abu dhabi',
  'sharjah',
]
const areaOf = (t: string) => {
  const a = AREAS.find((x) => t.includes(x))
  if (!a) return ''
  return a.length <= 3 ? a.toUpperCase() : cap(a)
}

const COLOURS = ['black', 'white', 'navy', 'beige', 'red', 'green', 'blue', 'pink', 'grey', 'brown', 'cream', 'olive']
const SIZES: [RegExp, string][] = [
  [/\b(xx?s|extra small)\b/, 'XS'],
  [/\b(xx?l|extra large)\b/, 'XL'],
  [/\b(small|size s)\b/, 'Small'],
  [/\b(medium|size m)\b/, 'Medium'],
  [/\b(large|size l)\b/, 'Large'],
  [/\bsize (\d{1,2})\b/, ''],
]

/* ---------- Per-sector narration ---------- */

function property(t: string): SearchNarration {
  const bedsMatch = t.match(/\b(\d)\s*-?\s*(?:bed(?:room)?s?|br)\b/)
  const beds = bedsMatch ? `${bedsMatch[1]}-bedroom` : /\bstudio\b/.test(t) ? '' : ''
  const typeMatch = t.match(/\b(apartment|villa|townhouse|studio|penthouse|flat|office)s?\b/)
  const type = typeMatch ? plural(typeMatch[1] === 'flat' ? 'apartment' : typeMatch[1]) : 'homes'
  const area = areaOf(t)
  const budget = budgetOf(t)
  const where = area ? ` in ${area}` : ''
  const noun = typeMatch ? type : 'homes'
  return {
    ack: area && typeMatch
      ? `Let me find some ${type}${where} for you.`
      : `Let me find some ${noun} that match.`,
    searching: `Searching ${[beds, type].filter(Boolean).join(' ')}${where}${budget ? ` ${budget}` : ''}`,
    long: `Still checking for more matching ${noun}`,
    closing: `I found these ${area ? 'homes' : noun}${where}. I can narrow them down further by bedrooms, budget, or building.`,
    noun,
  }
}

function apparel(t: string): SearchNarration {
  const colour = COLOURS.find((c) => new RegExp(`\\b${c}\\b`).test(t)) ?? ''
  const itemMatch = t.match(/\b(dress|shirt|t-shirt|jeans|trousers|abaya|kandura|jacket|sneaker|shoe|skirt|blazer|hoodie)(?:e?s)?\b/)
  const item = itemMatch ? plural(itemMatch[1] === 'dress' ? 'dresse' : itemMatch[1]) : ''
  let size = ''
  for (const [re, label] of SIZES) {
    const m = t.match(re)
    if (m) {
      size = label || `size ${m[1]}`
      break
    }
  }
  const mySize = /\bmy size\b/.test(t)
  const sized = Boolean(size || mySize)
  const what = [colour, item].filter(Boolean).join(' ')
  return {
    ack: sized ? 'Let me find some options in your size.' : 'Let me find some options for you.',
    searching: what
      ? `Searching ${what}${size ? ` in ${size}` : mySize ? ' in your size' : ''}${budgetOf(t) ? ` ${budgetOf(t)}` : ''}`
      : sized
        ? 'Searching available styles in your size'
        : 'Searching available styles',
    long: 'Still looking for more available styles',
    closing: `I found these options${sized ? ' in your size' : ''}. I can narrow them down by style, colour, or budget.`,
    noun: 'styles',
  }
}

function carRental(t: string): SearchNarration {
  const typeMatch = t.match(/\b(suv|sedan|4x4|van|pickup|hatchback|convertible|coupe|\d-seater)s?\b/)
  const type = typeMatch
    ? typeMatch[1] === 'suv'
      ? 'SUVs'
      : /\d-seater/.test(typeMatch[1])
        ? `${typeMatch[1]}s`
        : plural(typeMatch[1])
    : 'cars'
  const dated = /\b(today|tomorrow|tonight|weekend|next week|from|until|\d+\s*(day|days|week|weeks|month|months)|for (a|one) (day|week|month))\b/.test(t)
  return {
    ack: `Let me check which ${type} are available.`,
    searching: `Checking available ${type}${dated ? ' for your dates' : ''}`,
    long: `Still looking for more available ${type}`,
    closing: 'I found these available cars. I can narrow them down by type, price, or features.',
    noun: 'cars',
  }
}

function carsForSale(t: string): SearchNarration {
  const model = t.match(/\b(prado|patrol|land cruiser|range rover|camry|g-?class|model y|hilux)\b/)
  const type = /\bsuvs?\b/.test(t) ? 'SUVs' : ''
  const what = model ? cap(model[1]) : type || 'cars'
  const budget = budgetOf(t)
  return {
    ack: `Let me find some ${model ? `${what} listings` : what} that match.`,
    searching: `Searching ${model ? `${what} listings` : `${what} for sale`}${budget ? ` ${budget}` : ''}`,
    long: 'Still checking for more matching cars',
    closing: 'I found these cars for sale. I can narrow them down by budget, mileage, or colour.',
    noun: 'cars',
  }
}

function perfume(t: string): SearchNarration {
  if (isOpenQuestion(t)) {
    const q =
      'Here are some options currently available. What kind of fragrance are you looking for — fresh, woody, sweet, or something else?'
    return {
      ack: 'Let me check what’s available.',
      searching: 'Looking through the fragrances in stock',
      long: 'Still looking for more fragrances',
      closing: q,
      noun: 'fragrances',
      openQuestion: q,
    }
  }
  const family = t.match(/\b(woody|floral|fresh|oud|sweet|citrus|spicy|musky|amber|vanilla|aquatic)\b/)
  const audience = /\bwomen'?s?\b|\bfor women\b|\bladies\b/.test(t)
    ? 'women’s'
    : /\bmen'?s?\b|\bfor men\b/.test(t)
      ? 'men’s'
      : ''
  const budget = budgetOf(t)
  const specific = Boolean(family || audience || budget)
  return {
    ack: specific
      ? 'Let me find some fragrances that match.'
      : 'Let me see what fragrances match what you’re looking for.',
    searching: specific
      ? `Searching ${[audience, family?.[1]].filter(Boolean).join(' ')} fragrances${budget ? ` ${budget}` : ''}`.replace('  ', ' ')
      : 'Searching matching fragrances',
    long: 'Still looking for more matching fragrances',
    closing: 'I found these fragrances. I can narrow them down by scent profile, brand, or price.',
    noun: 'fragrances',
  }
}

function food(t: string): SearchNarration {
  const dish = t.match(/\b(burger|pizza|shawarma|sushi|biryani|pasta|salad|sandwich|wing|cake|dessert|breakfast)s?\b/)
  const near = /\bnear\b|\bnearby\b|\baround me\b/.test(t)
  return {
    ack: 'Let me see what’s available for you.',
    searching: dish
      ? `Looking for ${plural(dish[1])}${near ? ' near you' : ' on nearby menus'}`
      : 'Looking through available menu options',
    long: 'Still looking for more dishes nearby',
    closing: 'I found these options. I can help narrow them down by cuisine, price, or preference.',
    noun: 'dishes',
  }
}

function furniture(t: string): SearchNarration {
  const piece = t.match(/\b(sofa|desk|bed|dining table|chair|shelving|wardrobe|coffee table)s?\b/)
  const what = piece ? plural(piece[1]) : 'pieces'
  return {
    ack: `Let me find some ${piece ? what : 'pieces'} that fit.`,
    searching: `Searching ${what} available to deliver${budgetOf(t) ? ` ${budgetOf(t)}` : ''}`,
    long: `Still looking for more ${what}`,
    closing: 'I found these pieces. I can narrow them down by size, material, or delivery time.',
    noun: what,
  }
}

function electronics(t: string): SearchNarration {
  const item = t.match(
    /\b(gaming laptop|laptop|notebook|macbook|phone|smartphone|iphone|tablet|ipad|headphone|earbud|monitor|tv|television|console|camera|smartwatch)s?\b/,
  )
  const what = item
    ? item[1] === 'tv' || item[1] === 'television'
      ? 'TVs'
      : plural(item[1])
    : 'products'
  const budget = budgetOf(t)
  return {
    ack: 'Let me find some suitable options.',
    searching: item
      ? `Searching ${what}${budget ? ` ${budget}` : ''}`
      : 'Searching matching products',
    long: `Still looking for more matching ${item ? what.replace(/^gaming /, '') : 'options'}`,
    closing: 'I found these options. I can narrow them down further by specifications, brand, or budget.',
    noun: what,
  }
}

export function narrateSearch(
  query: string,
  category: NarrationCategory,
): SearchNarration {
  const t = query.toLowerCase()
  switch (category) {
    case 'property':
      return property(t)
    case 'apparel':
      return apparel(t)
    case 'car-rental':
      return carRental(t)
    case 'cars':
      return carsForSale(t)
    case 'perfume':
      return perfume(t)
    case 'food':
      return food(t)
    case 'furniture':
      return furniture(t)
    case 'electronics':
      return electronics(t)
  }
}
